/**
 * Datas da agenda sempre como texto "AAAA-MM-DD" e horas como minutos do dia.
 *
 * Nada aqui faz `new Date("AAAA-MM-DD")` para exibir: isso é meia-noite UTC e
 * vira o dia anterior em São Paulo (classe de bug da auditoria, ADR-0059).
 * As contas são feitas em UTC puro, onde não existe fuso nem horário de verão.
 */

export const DIAS_CURTOS = ["dom", "seg", "ter", "qua", "qui", "sex", "sáb"];
export const DIAS_LETRA = ["D", "S", "T", "Q", "Q", "S", "S"];
export const DIAS_LONGOS = [
  "Domingo", "Segunda-feira", "Terça-feira", "Quarta-feira", "Quinta-feira", "Sexta-feira", "Sábado",
];
export const MESES_CURTOS = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];
export const MESES_LONGOS = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
];

const ISO = /^(\d{4})-(\d{2})-(\d{2})$/;

function utc(iso) {
  const partes = ISO.exec(String(iso || ""));
  if (!partes) throw new Error(`Data inválida: ${iso}`);
  return new Date(Date.UTC(Number(partes[1]), Number(partes[2]) - 1, Number(partes[3])));
}

function paraIso(data) {
  return data.toISOString().slice(0, 10);
}

export const pad = (n) => String(n).padStart(2, "0");

export function somarDias(iso, dias) {
  const data = utc(iso);
  data.setUTCDate(data.getUTCDate() + dias);
  return paraIso(data);
}

export function somarMeses(iso, meses) {
  const data = utc(iso);
  data.setUTCDate(1);
  data.setUTCMonth(data.getUTCMonth() + meses);
  return paraIso(data);
}

/** 0 = domingo … 6 = sábado. */
export const diaDaSemana = (iso) => utc(iso).getUTCDay();
export const diaDoMes = (iso) => utc(iso).getUTCDate();
export const mes = (iso) => utc(iso).getUTCMonth();
export const ano = (iso) => utc(iso).getUTCFullYear();
export const primeiroDoMes = (iso) => `${String(iso).slice(0, 8)}01`;

/** `inicio`: 1 = semana começa na segunda (padrão), 7 = no domingo. */
export function inicioDaSemana(iso, inicio = 1) {
  const dia = diaDaSemana(iso);
  const recuo = inicio === 7 ? dia : (dia + 6) % 7;
  return somarDias(iso, -recuo);
}

export function diasEntre(de, ate) {
  return Math.round((utc(ate) - utc(de)) / 86400000);
}

export function intervalo(de, quantidade) {
  return Array.from({ length: quantidade }, (_, i) => somarDias(de, i));
}

/** "AAAA-MM-DD" de hoje no fuso informado (o do servidor manda na agenda). */
export function hojeNoFuso(fuso = "America/Sao_Paulo", agora = new Date()) {
  const partes = new Intl.DateTimeFormat("en-CA", {
    timeZone: fuso, year: "numeric", month: "2-digit", day: "2-digit",
  }).format(agora);
  return partes;
}

export function minutosAgoraNoFuso(fuso = "America/Sao_Paulo", agora = new Date()) {
  const [h, m] = new Intl.DateTimeFormat("en-GB", {
    timeZone: fuso, hour: "2-digit", minute: "2-digit", hourCycle: "h23",
  }).format(agora).split(":");
  return Number(h) * 60 + Number(m);
}

export function hm(minutos) {
  if (minutos === null || minutos === undefined) return "";
  const m = Math.max(0, Math.min(24 * 60, Math.round(minutos)));
  if (m >= 24 * 60) return "24:00";
  return `${pad(Math.floor(m / 60))}:${pad(m % 60)}`;
}

/** 107 -> "1h47"; 45 -> "45 min". */
export function duracao(minutos) {
  const m = Math.max(0, Math.round(minutos || 0));
  if (m < 60) return `${m} min`;
  const resto = m % 60;
  return `${Math.floor(m / 60)}h${resto ? pad(resto) : ""}`;
}

export const ddmm = (iso) => `${pad(diaDoMes(iso))}/${pad(mes(iso) + 1)}`;

/** "qui, 29 out" */
export const rotuloCurto = (iso) => `${DIAS_CURTOS[diaDaSemana(iso)]}, ${diaDoMes(iso)} ${MESES_CURTOS[mes(iso)]}`;

/** "Quinta-feira, 29 de outubro" */
export const rotuloLongo = (iso) => `${DIAS_LONGOS[diaDaSemana(iso)]}, ${diaDoMes(iso)} de ${MESES_LONGOS[mes(iso)].toLowerCase()}`;

/** "hoje", "amanhã", "ontem" ou "ter 27". */
export function relativo(iso, hoje) {
  if (!iso) return "sem data";
  if (iso === hoje) return "hoje";
  if (iso === somarDias(hoje, 1)) return "amanhã";
  if (iso === somarDias(hoje, -1)) return "ontem";
  return `${DIAS_CURTOS[diaDaSemana(iso)]} ${diaDoMes(iso)}`;
}

/** Arredonda para o encaixe de 15 min da grade. */
export const encaixe = (minutos, passo = 15) => Math.round(minutos / passo) * passo;

export const limitar = (valor, min, max) => Math.max(min, Math.min(max, valor));

/** "07:30" -> 450 (as preferências chegam como texto). */
export function minutosDeTexto(texto) {
  if (!texto) return null;
  const [h, m] = String(texto).split(":");
  return Number(h) * 60 + Number(m || 0);
}
